import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Product, ProductType } from '../../../../models/product/product';
import { productService } from '../../../../services/product-service';
import { Language } from '../../../../constants/language';
import ProductCard from './ProductCard';
import { Button } from '../../../ui';
import { ROUTES } from "../../../../constants/routes";

interface ProductsSectionProps {
  title: string;
  description: string;
  products?: Product[];
  productType?: ProductType;
  limit?: number;
  featured?: boolean;
  loading?: boolean;
  showCTA?: boolean;
  ctaTitle?: string;
  ctaDescription?: string;
  ctaButtonText?: string;
  ctaButtonLink?: string;
}

const ProductsSection: React.FC<ProductsSectionProps> = ({
  title,
  description,
  products: providedProducts,
  productType = ProductType.BOOK,
  limit = 3,
  featured = true,
  loading: providedLoading = false,
  showCTA = true,
  ctaTitle,
  ctaDescription,
  ctaButtonText,
  ctaButtonLink = ROUTES.STORE
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>(providedProducts || []);
  const [loading, setLoading] = useState(providedLoading);

  // Load products if not provided via props
  useEffect(() => {
    if (!providedProducts) {
      const loadProducts = async () => {
        try {
          setLoading(true);
          const result = await productService.getProducts(i18n.language as Language);

          let filteredProducts = result.data;

          if (productType) {
            filteredProducts = filteredProducts.filter(product => product.type === productType);
          }

          if (featured) {
            filteredProducts = filteredProducts.filter(product => product.featured);
          }

          if (limit) {
            filteredProducts = filteredProducts.slice(0, limit);
          }

          setProducts(filteredProducts);
        } catch (error) {
          console.error('Error loading products:', error);
          setProducts([]);
        } finally {
          setLoading(false);
        }
      };

      loadProducts();
    }
  }, [providedProducts, productType, featured, limit, i18n.language]);

  // Use provided products if available
  useEffect(() => {
    if (providedProducts) {
      let filteredProducts = [...providedProducts];

      if (productType) {
        filteredProducts = filteredProducts.filter(product => product.type === productType);
      }

      if (featured) {
        filteredProducts = filteredProducts.filter(product => product.featured);
      }

      if (limit) {
        filteredProducts = filteredProducts.slice(0, limit);
      }

      setProducts(filteredProducts);
    }
  }, [providedProducts, productType, featured, limit]);

  // Use provided loading state
  useEffect(() => {
    if (providedLoading !== undefined) {
      setLoading(providedLoading);
    }
  }, [providedLoading]);

  // Loading skeleton
  if (loading) {
    return (
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header skeleton */}
          <div className="text-center mb-16">
            <div className="w-16 h-16 bg-gray-200 animate-pulse rounded-full mx-auto mb-6"></div>
            <div className="h-12 bg-gray-200 animate-pulse rounded mx-auto mb-4 max-w-md"></div>
            <div className="h-6 bg-gray-200 animate-pulse rounded mx-auto max-w-2xl"></div>
          </div>

          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA skeleton */}
          {showCTA && (
            <div className="text-center mt-12">
              <div className="bg-gray-200 animate-pulse rounded-2xl p-8 max-w-2xl mx-auto h-48"></div>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white text-black rounded-full mb-6">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {title}
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                showAddToCart={false}
                showQuickView={true}
                onViewDetails={() => navigate(`/store/product/${product.id}`)}
              />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 text-gray-400 rounded-full mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-100 mb-2">
              {t('products.no-products')}
            </h3>
            <p className="text-gray-300">
              {t('products.loading')}
            </p>
          </div>
        )}

        {/* Call to Action Section */}
        {showCTA && products.length > 0 && (
          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 max-w-2xl mx-auto border border-gray-600 shadow-lg">
              <h3 className="text-2xl font-bold text-white mb-4">
                {ctaTitle}
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {ctaDescription}
              </p>
              <Link to={ctaButtonLink}>
                <Button
                  size="lg"
                  className="bg-white text-black hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  {ctaButtonText || (t('products.view-all'))}
                  <svg className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M13 7l5 5m0 0l-5 5m5-5H6"} />
                  </svg>
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
